// src/lib/api.js
// All external API calls go through here. Only owner/admin can call these.

import { supabase } from './supabase';

// ============================================================
// RATE LIMIT TRACKING
// ============================================================
export async function getApiUsage(provider, windowHours = 24) {
  const since = new Date(Date.now() - windowHours * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('api_call_log')
    .select('calls_used, created_at')
    .eq('api_provider', provider)
    .gte('created_at', since);

  if (error) return { used: 0, remaining: null };
  const used = data.reduce((sum, r) => sum + (r.calls_used || 1), 0);
  return { used, remaining: null }; // remaining calculated in UI based on limits
}

async function logApiCall({ provider, endpoint, cusips, callType, status, callsUsed = 1, callsRemaining, responseTimeMs, errorMessage }) {
  const { data: { user } } = await supabase.auth.getUser();
  await supabase.from('api_call_log').insert({
    called_by: user?.id,
    api_provider: provider,
    endpoint,
    cusips_requested: cusips,
    call_type: callType,
    status,
    calls_used: callsUsed,
    calls_remaining: callsRemaining,
    response_time_ms: responseTimeMs,
    error_message: errorMessage
  });
}

// ============================================================
// CUSIP → TICKER LOOKUP via OpenFIGI (free, 250/day)
// ============================================================
export function detectIdType(raw) {
  const v = (raw || '').trim().toUpperCase();

  // OCC option symbols (root 1–6 letters, YYMMDD, C/P, 8-digit strike)
  // Many sources omit space-padding in the root, so total length is often 19–21.
  if (/^[A-Z]{1,6}\d{6}[CP]\d{8}$/.test(v)) return 'occ';

  // FIGI (common prefix BBG, 12 alphanumerics)
  if (v.length === 12 && v.startsWith('BBG') && /^[A-Z0-9]{12}$/.test(v)) return 'figi';

  // ISIN (validate via Luhn so we don't misclassify as FIGI)
  if (isValidISIN(v)) return 'isin';

  // CUSIP (9 alphanumerics)
  if (v.length === 9 && /^[A-Z0-9]{9}$/.test(v)) return 'cusip';

  // Generic FIGI fallback (12 alphanumerics that aren't valid ISINs)
  if (v.length === 12 && /^[A-Z0-9]{12}$/.test(v)) return 'figi';

  // Otherwise treat as ticker/symbol and let the backend resolve.
  return 'ticker';
}

export function idTypeLabel(t) {
  return { cusip: 'CUSIP', isin: 'ISIN', figi: 'FIGI', occ: 'Option', ticker: 'Ticker' }[t] || 'Identifier';
}

function isValidISIN(v) {
  if (!(v.length === 12 && /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/.test(v))) return false;

  // Expand letters A=10..Z=35 into digits
  let digits = '';
  for (const ch of v) {
    if (ch >= '0' && ch <= '9') digits += ch;
    else digits += String(ch.charCodeAt(0) - 55);
  }

  // Luhn over expanded digits
  let sum = 0;
  let dbl = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
}

function parseOccSymbol(v) {
  if (!/^[A-Z]{1,6}\d{6}[CP]\d{8}$/.test(v)) return null;
  const root = v.slice(0, v.length - 15);         // 1–6 chars
  const yymmdd = v.slice(v.length - 15, v.length - 9); // 6 chars
  const cp = v.slice(v.length - 9, v.length - 8);      // C or P
  const strikeInt = parseInt(v.slice(v.length - 8), 10);
  const strike = Number.isFinite(strikeInt) ? strikeInt / 1000 : null;

  // Convert YYMMDD to ISO date; assume 2000–2099
  const yy = parseInt(yymmdd.slice(0, 2), 10);
  const mm = parseInt(yymmdd.slice(2, 4), 10);
  const dd = parseInt(yymmdd.slice(4, 6), 10);
  const year = 2000 + (Number.isFinite(yy) ? yy : 0);
  const expiry = (Number.isFinite(mm) && Number.isFinite(dd))
    ? `${year.toString().padStart(4,'0')}-${String(mm).padStart(2,'0')}-${String(dd).padStart(2,'0')}`
    : null;

  return { underlying: root, option_type: cp === 'C' ? 'call' : 'put', strike, expiry };
}
export async function lookupCusip(identifier) {
  const start = Date.now();
  const v = identifier.trim().toUpperCase();
  try {
    if (v === 'CASH000000' || v === 'CASH') return { ticker: 'CASH', name: 'Cash / USD', marketSector: 'Other' };
    const idType = detectIdType(v);
    const { data, error } = await supabase.functions.invoke('security-lookup', { body: { idType, idValue: v } });
    const elapsed = Date.now() - start;
    if (error) throw new Error(error.message);
    const results = data?.results || [];
    const result = (idType === 'occ')
      ? (results.find(r => String(r.securityType||'').toLowerCase().includes('option')) || results[0])
      : (results.find(r => r.securityType === 'Common Stock') || results[0]);
    if ((!results || results.length === 0) && idType === 'occ') {
  const parsed = parseOccSymbol(v);
  if (parsed) {
    const name = `${parsed.underlying} ${parsed.expiry || ''} ${parsed.option_type === 'call' ? 'C' : 'P'} ${parsed.strike ?? ''}`.trim();
    await logApiCall({ provider: 'openfigi', endpoint: '/v3/mapping', cusips: [v], callType: 'security_lookup', status: 'fallback_parsed', responseTimeMs: elapsed });
    return {
      ticker: parsed.underlying,
      name,
      exchCode: null,
      securityType: 'Option',
      marketSector: 'Equity',
      detectedIdType: idType,
      option_type: parsed.option_type,
      strike: parsed.strike,
      expiry: parsed.expiry
    };
  }
}
await logApiCall({ provider: 'openfigi', endpoint: '/v3/mapping', cusips: [v], callType: 'security_lookup', status: result ? 'success' : 'not_found', responseTimeMs: elapsed });
    if (!result) return null;
    const parsed = (idType === 'occ') ? parseOccSymbol(v) : null;
    return {
      ticker: result.ticker,
      name: result.name,
      exchCode: result.exchCode,
      securityType: result.securityType,
      marketSector: result.marketSector,
      detectedIdType: idType,
      ...(parsed ? { option_type: parsed.option_type, strike: parsed.strike, expiry: parsed.expiry, underlying: parsed.underlying } : {})
    };
  } catch (err) {
    await logApiCall({ provider: 'openfigi', endpoint: '/v3/mapping', cusips: [v], callType: 'security_lookup', status: 'error', errorMessage: err.message });
    return null;
  }
}


// ============================================================
// PRICE FETCHING via Yahoo Finance (free, unofficial)
// Uses the public chart API which doesn't require a key
// ============================================================
export async function fetchLivePrices(tickers) {
  const start = Date.now();
  const results = {};
  await Promise.all(tickers.map(async (ticker) => {
    if (ticker === 'CASH') { results['CASH'] = { price: 1.0, currency: 'USD', name: 'Cash' }; return; }
    try {
      const { data, error } = await supabase.functions.invoke('security-lookup', { body: { action: 'live_price', ticker } });
      if (error || !data?.price) throw new Error(error?.message || 'no price');
      results[ticker] = data;
    } catch (e) { results[ticker] = { error: e.message }; }
  }));
  const elapsed = Date.now() - start;
  const ok = Object.values(results).filter(r => !r.error).length;
  await logApiCall({ provider: 'yahoo_finance', endpoint: '/v8/finance/chart', cusips: tickers, callType: 'price_refresh', status: ok > 0 ? 'success' : 'error', callsUsed: tickers.length, responseTimeMs: elapsed });
  return results;
}


// ============================================================
// HISTORICAL PRICES (for risk calculations, 1yr lookback)
// ============================================================
export async function fetchHistoricalPrices(ticker, months = 12) {
  if (!ticker || ticker === 'CASH') return [];
  const start = Date.now();
  try {
    const { data, error } = await supabase.functions.invoke('security-lookup', { body: { action: 'historical', ticker, months } });
    const elapsed = Date.now() - start;
    if (error) throw new Error(error.message);
    const prices = data?.prices || [];
    await logApiCall({ provider: 'yahoo_finance', endpoint: '/v8/finance/chart (historical)', cusips: [ticker], callType: 'historical', status: prices.length > 0 ? 'success' : 'not_found', callsUsed: 1, responseTimeMs: elapsed });
    return prices;
  } catch (err) {
    await logApiCall({ provider: 'yahoo_finance', endpoint: '/v8/finance/chart (historical)', cusips: [ticker], callType: 'historical', status: 'error', errorMessage: err.message });
    return [];
  }
}


// ============================================================
// FAMA-FRENCH FACTOR DATA (free from Ken French's library via a CORS proxy)
// Falls back to a static recent estimate if unavailable
// ============================================================
export async function fetchFamaFrenchFactors() {
  // Returns monthly FF5 factor returns (Market, SMB, HML, RMW, CMA, MOM, RF)
  // This uses a public endpoint that mirrors Ken French's CSV data
  try {
    const res = await fetch('https://mba.tuck.dartmouth.edu/pages/faculty/ken.french/ftp/F-F_Research_Data_5_Factors_2x3_daily_CSV.zip');
    // Note: this is a ZIP - in practice you'd use a backend proxy or preprocess this
    // For client-side, we provide a reasonable approximation using recent data
    // This is flagged in UI as "estimated" when live data unavailable
    return null; // triggers fallback in riskEngine
  } catch {
    return null;
  }
}


// ============================================================
// IMPLIED VOLATILITY (from option price, Black-Scholes)
// ============================================================
export function impliedVolatilityFromOptionPrice(optionPrice, S, K, T, r, optionType = 'call') {
  // Simple bisection solver; returns sigma (e.g. 0.25) or null.
  if (!(optionPrice > 0) || !(S > 0) || !(K > 0) || !(T > 0)) return null;

  const priceForSigma = (sigma) => {
    const g = _bsPrice(S, K, T, r, sigma, optionType);
    return g;
  };

  let lo = 1e-4;
  let hi = 5.0;
  const target = optionPrice;

  // ensure bracket
  const plo = priceForSigma(lo);
  const phi = priceForSigma(hi);
  if (target < plo) return lo;
  if (target > phi) return hi;

  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    const pmid = priceForSigma(mid);
    if (!Number.isFinite(pmid)) return null;
    if (pmid > target) hi = mid; else lo = mid;
    if (Math.abs(pmid - target) / target < 1e-4) return mid;
  }
  return (lo + hi) / 2;
}

function _bsPrice(S, K, T, r, sigma, optionType) {
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  const Nd1 = _normCdf(optionType === 'call' ? d1 : -d1);
  const Nd2 = _normCdf(optionType === 'call' ? d2 : -d2);
  const discK = K * Math.exp(-r * T);

  if (optionType === 'call') return S * _normCdf(d1) - discK * _normCdf(d2);
  return discK * _normCdf(-d2) - S * _normCdf(-d1);
}

function _normCdf(x) {
  // Abramowitz-Stegun approximation
  const k = 1 / (1 + 0.2316419 * Math.abs(x));
  const kSum = k * (0.319381530 + k * (-0.356563782 + k * (1.781477937 + k * (-1.821255978 + k * 1.330274429))));
  const c = 1 - (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * x * x) * kSum;
  return x >= 0 ? c : 1 - c;
}


// ============================================================
// OPTION GREEKS CALCULATOR (Black-Scholes, client-side)
// ============================================================
export function calculateGreeks(S, K, T, r, sigma, optionType = 'call') {
  // S = stock price, K = strike, T = time to expiry (years), r = risk-free rate, sigma = IV
  if (T <= 0) return { delta: optionType === 'call' ? (S > K ? 1 : 0) : (S < K ? -1 : 0), gamma: 0, theta: 0, vega: 0, rho: 0 };

  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;

  const Nd1 = normalCDF(d1);
  const Nd2 = normalCDF(d2);
  const Nd1n = normalCDF(-d1);
  const Nd2n = normalCDF(-d2);
  const pdf_d1 = normalPDF(d1);

  const delta = optionType === 'call' ? Nd1 : Nd1 - 1;
  const gamma = pdf_d1 / (S * sigma * sqrtT);
  const theta_call = (-(S * pdf_d1 * sigma) / (2 * sqrtT) - r * K * Math.exp(-r * T) * Nd2) / 365;
  const theta_put = (-(S * pdf_d1 * sigma) / (2 * sqrtT) + r * K * Math.exp(-r * T) * Nd2n) / 365;
  const theta = optionType === 'call' ? theta_call : theta_put;
  const vega = S * pdf_d1 * sqrtT / 100; // per 1% move in IV
  const rho_call = K * T * Math.exp(-r * T) * Nd2 / 100;
  const rho_put = -K * T * Math.exp(-r * T) * Nd2n / 100;
  const rho = optionType === 'call' ? rho_call : rho_put;

  return { delta, gamma, theta, vega, rho };
}

function normalCDF(x) {
  const a1=0.254829592,a2=-0.284496736,a3=1.421413741,a4=-1.453152027,a5=1.061405429,p=0.3275911;
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x) / Math.sqrt(2);
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1.0 + sign * y);
}

function normalPDF(x) { return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI); }
